import { useState, useCallback, useEffect, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { getApiErrorMessage } from '../../utils/apiErrors';
import swal from '../../utils/swal';
import './Agenda.css';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import CancelIcon from '@mui/icons-material/Cancel';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CheckIcon from '@mui/icons-material/Check';
import Loader from '../../components/loading/Loader';
import CambioTurnoSelector from '../../components/cambioTurno/CambioTurnoSelector';

dayjs.locale('es');
const localizer = dayjsLocalizer(dayjs);

// Duración por defecto de un turno (el modelo no la define todavía)
const DURACION_MIN = 30;

const messages = {
    date: 'Fecha',
    time: 'Hora',
    event: 'Turno',
    allDay: 'Todo el día',
    week: 'Semana',
    work_week: 'Semana laboral',
    day: 'Día',
    month: 'Mes',
    previous: 'Anterior',
    next: 'Siguiente',
    yesterday: 'Ayer',
    tomorrow: 'Mañana',
    today: 'Hoy',
    agenda: 'Agenda',
    noEventsInRange: 'No hay turnos en este período.',
    showMore: total => `+ ${total} más`,
};

// Color del evento según el estado del turno
function colorPorEstado(estado) {
    switch (estado) {
        case 'RESERVADO': return '#1976d2';
        case 'CONFIRMADO': return '#0288d1';
        case 'REALIZADO': return '#2e7d32';
        case 'CANCELADO': return '#9e9e9e';
        default: return '#607d8b';
    }
}

function obtenerNombreServicio(turno) {
    return turno.especialidad?.nombre ?? turno.practica?.nombre ?? '-';
}

function Agenda() {
    const { user } = useAuth();
    const usuarioId = user?.usuarioId;

    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
    const [mostrarModalCancelar, setMostrarModalCancelar] = useState(false);
    const [motivoCancelacion, setMotivoCancelacion] = useState('');
    const [mostrarModalCambio, setMostrarModalCambio] = useState(false);
    const [turnosCambioDisponibles, setTurnosCambioDisponibles] = useState([]);
    const [turnoCambioSeleccionado, setTurnoCambioSeleccionado] = useState(null);
    const [cargandoTurnosCambio, setCargandoTurnosCambio] = useState(false);
    const [errorCambio, setErrorCambio] = useState('');
    const [procesando, setProcesando] = useState(false);

    const [view, setView] = useState('week');
    const [date, setDate] = useState(new Date());

    const navigate = useNavigate();

    const cargarTurnos = useCallback(async () => {
        if (!usuarioId) return;

        setLoading(true);
        try {
            const res = await axiosInstance.get(
                `/turnos/medico?usuarioId=${usuarioId}&estado=RESERVADO`
            );
            setTurnos(res.data.data ?? []);
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudieron cargar los turnos. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setLoading(false);
        }
    }, [usuarioId]);

    useEffect(() => {
        cargarTurnos();
    }, [cargarTurnos]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setMostrarModalCancelar(false);
                setMostrarModalCambio(false);
                setTurnoSeleccionado(null);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, []);

    const eventos = useMemo(() => {
        return turnos.map(turno => {
            const start = new Date(turno.fechaHora);
            const end = new Date(start.getTime() + DURACION_MIN * 60000);
            return {
                id: turno._id,
                title: turno.paciente?.nombre || 'Turno',
                start,
                end,
                resource: turno,
            };
        });
    }, [turnos]);

    const eventPropGetter = useCallback((event) => ({
        style: {
            backgroundColor: colorPorEstado(event.resource.estado),
            borderRadius: '6px',
            border: 'none',
            color: '#fff',
            opacity: event.resource.estado === 'CANCELADO' ? 0.6 : 1,
        },
    }), []);

    async function marcarRealizado(turnoId) {
        setProcesando(true);
        try {
            await axiosInstance.patch(`/turnos/${turnoId}/realizado`);
            await cargarTurnos();
            setTurnoSeleccionado(null);
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudo marcar el turno como realizado. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setProcesando(false);
        }
    }

    async function confirmarCancelacion() {
        setProcesando(true);
        try {
            await axiosInstance.patch(
                `/turnos/${turnoSeleccionado._id}/cancelar`,
                { motivo: motivoCancelacion }
            );
            await cargarTurnos();
            setMotivoCancelacion('');
            setMostrarModalCancelar(false);
            setTurnoSeleccionado(null);
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudo cancelar el turno. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setProcesando(false);
        }
    }

    async function abrirModalCambio() {
        if (!turnoSeleccionado) return;

        setTurnoCambioSeleccionado(null);
        setTurnosCambioDisponibles([]);
        setErrorCambio('');
        setMostrarModalCambio(true);
        setCargandoTurnosCambio(true);

        try {
            const res = await axiosInstance.get(`/turnos/${turnoSeleccionado._id}/cambio_fecha/disponibles`);
            setTurnosCambioDisponibles(res.data.data ?? []);
        } catch (err) {
            const mensaje = err.response?.data?.message ?? 'No se pudieron consultar los turnos disponibles.';
            setErrorCambio(mensaje);
        } finally {
            setCargandoTurnosCambio(false);
        }
    }

    async function confirmarCambio() {
        if (!turnoSeleccionado || !turnoCambioSeleccionado) return;

        setProcesando(true);
        setErrorCambio('');

        try {
            await axiosInstance.patch(
                `/turnos/${turnoSeleccionado._id}/cambio_fecha/solicitar`,
                { nuevaFechaHora: turnoCambioSeleccionado.fechaHora }
            );

            await cargarTurnos();
            setMostrarModalCambio(false);
            setTurnoSeleccionado(null);
            setTurnoCambioSeleccionado(null);
            setTurnosCambioDisponibles([]);
        } catch (err) {
            const mensaje = getApiErrorMessage(err, 'No se pudo solicitar el cambio de turno.');
            setErrorCambio(mensaje);
        } finally {
            setProcesando(false);
        }
    }

    if (loading) return <Loader mensaje="Cargando turnos..." />;

    return (
        <div className="agenda">

            <header className="agenda__header">
                <div>
                    AGENDA
                </div>

                <div className="agenda-toolbar">
                    <button
                        className="btn-solicitudes"
                        onClick={() => navigate('/solicitudes-cambio')}
                    >
                        Solicitudes de Cambio
                    </button>
                </div>
            </header>

            <section className="agenda__section">
                <div className="agenda__calendar">
                    <Calendar
                        localizer={localizer}
                        events={eventos}
                        startAccessor="start"
                        endAccessor="end"
                        messages={messages}
                        culture="es"
                        view={view}
                        onView={setView}
                        date={date}
                        onNavigate={setDate}
                        views={['month', 'week', 'day', 'agenda']}
                        popup
                        eventPropGetter={eventPropGetter}
                        onSelectEvent={(event) => setTurnoSeleccionado(event.resource)}
                        min={new Date(1970, 0, 1, 7, 0, 0)}
                        max={new Date(1970, 0, 1, 21, 0, 0)}
                    />
                </div>

            </section>

            {/* Modal de detalle del turno */}
            {turnoSeleccionado && !mostrarModalCancelar && !mostrarModalCambio && (
                <div
                    className="modal-overlay"
                    onClick={() => setTurnoSeleccionado(null)}
                >
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Detalle del turno</h3>

                        <div className="agenda__detalle">
                            <div className="agenda__row">
                                <EventIcon className="icon" />
                                <span>
                                    {new Date(turnoSeleccionado.fechaHora).toLocaleString('es-AR', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </span>
                            </div>

                            <div className="agenda__row">
                                <PersonIcon className="icon" />
                                <span>Paciente: {turnoSeleccionado.paciente?.nombre}</span>
                            </div>

                            <div className="agenda__row">
                                <LocationOnIcon className="icon" />
                                <span>Sede: {turnoSeleccionado.sede?.nombre}</span>
                            </div>

                            <div className="agenda__row">
                                <LocalHospitalIcon className="icon" />
                                <span>
                                    {obtenerNombreServicio(turnoSeleccionado)}
                                </span>
                            </div>

                            <div className={`estado estado--${turnoSeleccionado.estado.toLowerCase()}`}>
                                <strong>{turnoSeleccionado.estado}</strong>
                            </div>
                        </div>

                        <div className="agenda__actions">
                            <button
                                className="btn btn-success"
                                onClick={() => marcarRealizado(turnoSeleccionado._id)}
                                disabled={procesando}
                            >
                                {procesando
                                    ? <Loader inline />
                                    : <><CheckIcon fontSize="small" />Realizado</>}
                            </button>

                            <button
                                className="btn btn-danger"
                                onClick={() => setMostrarModalCancelar(true)}
                                disabled={procesando}
                            >
                                <CancelIcon fontSize="small" />
                                Cancelar
                            </button>

                            <button
                                className="btn btn-warning"
                                onClick={abrirModalCambio}
                                disabled={procesando}
                            >
                                <EditCalendarIcon fontSize="small" />
                                Cambio
                            </button>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn"
                                onClick={() => setTurnoSeleccionado(null)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de cancelación */}
            {mostrarModalCancelar && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Cancelar Turno</h3>

                        <textarea
                            value={motivoCancelacion}
                            onChange={e => setMotivoCancelacion(e.target.value)}
                            placeholder="Motivo de cancelación"
                            rows={4}
                        />

                        <div className="modal-actions">
                            <button
                                className="btn"
                                onClick={() => {
                                    setMostrarModalCancelar(false);
                                    setMotivoCancelacion('');
                                }}
                                disabled={procesando}
                            >
                                Volver
                            </button>

                            <button
                                className="btn btn-danger"
                                onClick={confirmarCancelacion}
                                disabled={!motivoCancelacion.trim() || procesando}
                            >
                                {procesando ? <Loader inline /> : 'Confirmar Cancelación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {mostrarModalCambio && turnoSeleccionado && (
                <div
                    className="modal-overlay"
                    onClick={() => setMostrarModalCambio(false)}
                >
                    <div className="modal modal-cambio" onClick={(e) => e.stopPropagation()}>
                        <h3>Solicitar cambio de turno</h3>

                        <div className="agenda-cambio-resumen">
                            <div className="agenda__row">
                                <EventIcon className="icon" />
                                <span>
                                    Turno actual:{' '}
                                    {new Date(turnoSeleccionado.fechaHora).toLocaleString('es-AR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </span>
                            </div>

                            <div className="agenda__row">
                                <PersonIcon className="icon" />
                                <span>Paciente: {turnoSeleccionado.paciente?.nombre}</span>
                            </div>

                            <div className="agenda__row">
                                <LocationOnIcon className="icon" />
                                <span>Sede: {turnoSeleccionado.sede?.nombre}</span>
                            </div>
                        </div>

                        <div className="agenda-cambio-scroll">
                            {cargandoTurnosCambio ? (
                                <Loader mensaje="Buscando turnos disponibles..." />
                            ) : errorCambio ? (
                                <p className="agenda-error">{errorCambio}</p>
                            ) : turnosCambioDisponibles.length === 0 ? (
                                <p>No hay turnos disponibles para solicitar el cambio.</p>
                            ) : (
                                <CambioTurnoSelector
                                    turnos={turnosCambioDisponibles}
                                    seleccionado={turnoCambioSeleccionado}
                                    onSelect={setTurnoCambioSeleccionado}
                                />
                            )}
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn"
                                onClick={() => {
                                    setMostrarModalCambio(false);
                                    setTurnoCambioSeleccionado(null);
                                    setErrorCambio('');
                                }}
                                disabled={procesando}
                            >
                                Volver
                            </button>

                            <button
                                className="btn btn-warning"
                                onClick={confirmarCambio}
                                disabled={!turnoCambioSeleccionado || procesando}
                            >
                                {procesando ? <Loader inline /> : 'Solicitar cambio'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default Agenda;
